/**
 * NOTE: Some of these fields are used to track AI MAU
 * See this page for more details: https://hello.atlassian.net/wiki/spaces/AAI/pages/3770721410/Unified+event+Instrumentation+for+AI
 */

import type { ChatEntryPoint } from '@atlaskit/rovo-triggers/chat-entry-point';
import type { RovoChatOpenMode } from '@atlaskit/rovo-triggers/params-types';

import type { FollowUpSource } from '../constants';

export type ConversationAssistantUIModes = RovoChatOpenMode | undefined;

/**
 * Indicates where an explicitly invoked skill was selected from in the chat input.
 * Used to populate the `skillSelectionSource` attribute on `skillsInvoked` analytics
 * entries when `invocationType` is `'explicit'`.
 *
 * Stored on `AnalyticsPropertiesContext` so any skill insertion surface can write the
 * source at the moment of selection without forcing a re-render, and the chat input
 * can read it at submit time when constructing the analytics payload.
 */
export type SkillSelectionSource =
	| 'editorTypeahead'
	| 'inlineBrowse'
	| 'autoSuggest'
	| 'rovoChatAction'
	| 'promptLibrary'
	| 'scheduledChatIdea'
	| 'skillsModal';

export const AI_MATE_ATTRIBUTES = {
	agentCreatorType: 'agentCreatorType',
	agentId: 'agentId',
	agentVisibility: 'agentVisibility',
	agentExternalConfigReference: 'agentExternalConfigReference',
	agentActions: 'agentActions',
	agentIsDefault: 'agentIsDefault',
	agentIsVerified: 'agentIsVerified',
	conversationChannelId: 'conversationChannelId',
	chatEntryPoint: 'chatEntryPoint',
	experienceId: 'experienceId',
	fullScreen: 'fullScreen',
	mode: 'mode',
	product: 'product',
	productHeader: 'productHeader',
	inProductExperience: 'inProductExperience',
	singleInstrumentationID: 'singleInstrumentationID',
	sharedFeaturesInstrumentationId: 'sharedFeaturesInstrumentationId',
	assistanceServiceConfigMinionAlias: 'assistanceServiceConfigMinionAlias',
	skillSelectionSource: 'skillSelectionSource',
	// The Rovo Space a chat is scoped to (when any). Set on every event fired
	// while a space-scoped chat is active so "Space MAU" (unique users who
	// initiate a chat in a space) can be measured downstream.
	spaceId: 'spaceId',
	// Source surface that launched the conversation; emitted on result events only.
	// Runtime-validated against the launch-source allowlists at the pubsub boundary, so
	// only closed-vocabulary values ever reach analytics despite FollowUpSource's
	// `| string` fallback.
	launchSource: 'launchSource',
	// Quick Find-to-Chat journey correlation. Emitted only on the five funnel events
	// allowlisted by the analytics controller while the attributed conversation is active.
	rovoJourneyId: 'rovoJourneyId',
	// Set once a conversation is started from a third-party suggested prompt in the
	// Create-with-Rovo modal, so downstream events the 3P experiment does not own
	// (`message/sent`, `adopted/contentFinalized`) can be segmented by cohort.
	isThirdPartyPromptConversation: 'isThirdPartyPromptConversation',
	thirdPartyPromptCohort: 'thirdPartyPromptCohort',
	// Third-party apps connected from the in-thread connect prompt so far in this
	// conversation, so a later message can be read as post-connect without a join.
	thirdPartyConnectCount: 'thirdPartyConnectCount',
} as const;

export type AIMateAgentAttributeType = {
	[AI_MATE_ATTRIBUTES.agentCreatorType]: string | null;
	[AI_MATE_ATTRIBUTES.agentId]: string | null;
	[AI_MATE_ATTRIBUTES.agentVisibility]: string | null;
	[AI_MATE_ATTRIBUTES.agentExternalConfigReference]: string | null;
	[AI_MATE_ATTRIBUTES.agentActions]: string[];
	[AI_MATE_ATTRIBUTES.agentIsDefault]: boolean | null;
	[AI_MATE_ATTRIBUTES.agentIsVerified]: boolean | null;
};

export type AIMateAttributeType = AIMateAgentAttributeType & {
	[AI_MATE_ATTRIBUTES.conversationChannelId]: string;
	[AI_MATE_ATTRIBUTES.chatEntryPoint]?: ChatEntryPoint;
	[AI_MATE_ATTRIBUTES.experienceId]: string;
	[AI_MATE_ATTRIBUTES.fullScreen]: boolean;
	[AI_MATE_ATTRIBUTES.mode]: string | undefined;
	[AI_MATE_ATTRIBUTES.product]: string;
	[AI_MATE_ATTRIBUTES.productHeader]: string;
	[AI_MATE_ATTRIBUTES.inProductExperience]: string | undefined;
	[AI_MATE_ATTRIBUTES.singleInstrumentationID]: string;
	[AI_MATE_ATTRIBUTES.sharedFeaturesInstrumentationId]?: string;
	[AI_MATE_ATTRIBUTES.assistanceServiceConfigMinionAlias]?: string | null;
	[AI_MATE_ATTRIBUTES.skillSelectionSource]?: SkillSelectionSource;
	[AI_MATE_ATTRIBUTES.spaceId]?: string;
	[AI_MATE_ATTRIBUTES.launchSource]?: FollowUpSource;
	[AI_MATE_ATTRIBUTES.rovoJourneyId]?: string;
	[AI_MATE_ATTRIBUTES.isThirdPartyPromptConversation]?: boolean;
	[AI_MATE_ATTRIBUTES.thirdPartyPromptCohort]?: string;
	[AI_MATE_ATTRIBUTES.thirdPartyConnectCount]?: number;
};

export type AnalyticsPropertiesContextProps = {
	propertiesRef: React.MutableRefObject<AIMateAttributeType>;
	mode: ConversationAssistantUIModes;
	setAnalyticsPropertiesWithoutRerender: (properties: Partial<AIMateAttributeType>) => void;
};

export type SharedFeaturesInstrumentationIdContextProps = {
	sharedFeaturesInstrumentationId: string | undefined;
	isInSmartCreateFlow: boolean | undefined;
};
